let modalLockCount = 0
let lockedScrollTop = 0
let lockedScrollLeft = 0
let isSettingScroll = false
let originalOverflowY = ''
let originalBodyWidth = ''
let originalBodyBoxSizing = ''
let hasBodyWidthCompensationSnapshot = false
let scrollRestoreToken = 0

const hasRootVerticalScrollbar = () => {
  const scrollEl = document.scrollingElement || document.documentElement
  return scrollEl.scrollHeight > window.innerHeight
}

/**
 * @description 解析滚动锁期间根节点应使用的纵向 overflow
 * @param {boolean} shouldReserveScrollbar - 页面锁定前是否存在纵向滚动溢出
 * @returns {string} 锁定期间的 overflow-y
 * @reason 根节点从 visible 强制切换到 scroll 会让 vw 扣除滚动条宽度；改用 auto
 * 可保留条件滚动条和原有 vw 基准，同时阻止 body 的 overflow 向视口传播。
 */
const resolveLockedRootOverflowY = (shouldReserveScrollbar) => {
  if (!shouldReserveScrollbar) return 'hidden'

  const currentOverflowY = window.getComputedStyle(document.documentElement).overflowY
  return currentOverflowY === 'visible' ? 'auto' : currentOverflowY
}

/**
 * @description 清除 Arco Modal 为滚动条补偿写入的 body 宽度，避免固定布局横向抖动
 */
const removeBodyWidthCompensation = () => {
  if (!import.meta.client || modalLockCount === 0 || !hasBodyWidthCompensationSnapshot) return

  const { style } = document.body
  if (originalBodyWidth === '' && style.width) {
    style.removeProperty('width')
  }
  if (originalBodyBoxSizing === '' && style.boxSizing === 'border-box') {
    style.removeProperty('box-sizing')
  }
}

const captureBodyWidthCompensationSnapshot = () => {
  if (hasBodyWidthCompensationSnapshot) return

  originalBodyWidth = document.body.style.width
  originalBodyBoxSizing = document.body.style.boxSizing
  hasBodyWidthCompensationSnapshot = true
}

const scheduleBodyWidthCompensationCleanup = () => {
  if (!import.meta.client) return

  captureBodyWidthCompensationSnapshot()

  queueMicrotask(() => {
    removeBodyWidthCompensation()
    requestAnimationFrame(removeBodyWidthCompensation)
    window.setTimeout(removeBodyWidthCompensation, 50)
  })
}

/**
 * 需要阻止的导致滚动的键盘按键
 */
const SCROLL_KEYS = [
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'PageUp',
  'PageDown',
  'Home',
  'End',
  ' ',
  'Space',
]

/**
 * @description 检查元素及其祖先是否处于可滚动的容器内（排除 body 和 html）
 * @param {HTMLElement} el
 * @returns {boolean}
 */
const isInsideScrollableContainer = (el) => {
  let parent = el
  while (parent && parent !== document.body && parent !== document.documentElement) {
    const style = window.getComputedStyle(parent)
    const overflowY = style.overflowY
    const overflowX = style.overflowX

    const isScrollableY =
      (overflowY === 'auto' || overflowY === 'scroll') && parent.scrollHeight > parent.clientHeight
    const isScrollableX =
      (overflowX === 'auto' || overflowX === 'scroll') && parent.scrollWidth > parent.clientWidth

    if (isScrollableY || isScrollableX) {
      return true
    }
    parent = parent.parentNode
  }
  return false
}

/**
 * @description 强制滚动容器停留在锁定时的位置
 */
const enforceScrollPosition = () => {
  if (modalLockCount === 0 || isSettingScroll) return

  const scrollEl = document.scrollingElement
  const currentTop = scrollEl.scrollTop
  const currentLeft = scrollEl.scrollLeft

  if (currentTop !== lockedScrollTop || currentLeft !== lockedScrollLeft) {
    isSettingScroll = true
    scrollEl.scrollTo({
      top: lockedScrollTop,
      left: lockedScrollLeft,
      behavior: 'auto',
    })
    queueMicrotask(() => {
      isSettingScroll = false
    })
  }
}

/**
 * @description 通用事件阻止函数
 */
const preventDefaultHandler = (e) => {
  if (modalLockCount === 0) return
  if (isInsideScrollableContainer(e.target)) return
  if (e.cancelable) {
    e.preventDefault()
  }
}

/**
 * @description 键盘滚动阻止
 */
const preventKeyScroll = (e) => {
  if (modalLockCount === 0) return
  if (!SCROLL_KEYS.includes(e.key)) return

  const target = e.target
  const isInput =
    target &&
    (target.matches('input, textarea, [contenteditable="true"]') || target.isContentEditable)
  if (isInput) return
  if (isInsideScrollableContainer(target)) return

  if (e.cancelable) {
    e.preventDefault()
  }
}

/**
 * @description 内部清理函数，恢复页面原始状态
 */
const cleanup = ({ waitForBodyUnlock = false } = {}) => {
  if (!import.meta.client) return

  window.removeEventListener('scroll', scrollHandler)
  window.removeEventListener('wheel', preventDefaultHandler)
  window.removeEventListener('touchmove', preventDefaultHandler)
  window.removeEventListener('keydown', preventKeyScroll)

  const token = scrollRestoreToken
  const restoreOverflowY = () => {
    if (token !== scrollRestoreToken || modalLockCount > 0) return

    if (originalOverflowY === '') {
      document.documentElement.style.removeProperty('overflow-y')
    } else {
      document.documentElement.style.overflowY = originalOverflowY
    }

    isSettingScroll = false
    hasBodyWidthCompensationSnapshot = false
    originalBodyWidth = ''
    originalBodyBoxSizing = ''
  }

  if (waitForBodyUnlock) {
    const waitBodyUnlock = () => {
      if (token !== scrollRestoreToken || modalLockCount > 0) return
      if (document.body.style.overflow === 'hidden') {
        requestAnimationFrame(waitBodyUnlock)
        return
      }
      restoreOverflowY()
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(waitBodyUnlock)
    })
    return
  }

  restoreOverflowY()
}

/**
 * @description scroll 事件监听处理
 */
const scrollHandler = () => {
  if (modalLockCount > 0) {
    enforceScrollPosition()
  }
}

/**
 * @description 锁定 body 滚动，但允许其他局部区域（如弹窗、侧边栏）正常滚动
 */
export const lockBodyScroll = (options = {}) => {
  if (!import.meta.client) return

  if (modalLockCount === 0) {
    try {
      const scrollEl = document.scrollingElement
      lockedScrollTop = scrollEl.scrollTop
      lockedScrollLeft = scrollEl.scrollLeft
      scrollRestoreToken += 1

      originalOverflowY = document.documentElement.style.overflowY
      const shouldReserveScrollbar = hasRootVerticalScrollbar()
      document.documentElement.style.overflowY = resolveLockedRootOverflowY(shouldReserveScrollbar)

      // 仅在锁定期间通过监听器控制事件
      window.addEventListener('scroll', scrollHandler, { passive: false })
      window.addEventListener('wheel', preventDefaultHandler, { passive: false })
      window.addEventListener('touchmove', preventDefaultHandler, { passive: false })
      window.addEventListener('keydown', preventKeyScroll, { passive: false })
    } catch (error) {
      console.warn('[lockBodyScroll] 锁定滚动失败:', error)
    }
  }
  modalLockCount += 1

  if (options.cleanupBodyWidthCompensation !== false) {
    scheduleBodyWidthCompensationCleanup()
  }
}

/**
 * @description 解锁 body 滚动
 */
export const unlockBodyScroll = (options = {}) => {
  if (!import.meta.client || modalLockCount === 0) return

  modalLockCount -= 1
  if (modalLockCount === 0) {
    try {
      cleanup({ waitForBodyUnlock: Boolean(options.waitForBodyUnlock) })
    } catch (error) {
      console.warn('[unlockBodyScroll] 解锁滚动失败:', error)
    }
  }
}

/**
 * @description 强制清除所有滚动锁定（通常用于路由跳转、异常处理等场景）
 */
export const clearAllBodyScrollLocks = () => {
  if (!import.meta.client) return
  if (modalLockCount > 0) {
    modalLockCount = 0
    scrollRestoreToken += 1
    cleanup()
  }
}
